/**
 * Κεντρικές προσφορές οργανισμού.
 *
 * ΜΙΑ εγγραφή που ισχύει σε ΟΛΑ τα καταστήματα — όχι αντίγραφα ανά κατάστημα
 * (σε αντίθεση με τον κατάλογο, που υλοποιεί προϊόντα σε κάθε κατάστημα).
 * Αλλάζεις την αξία μία φορά και ισχύει παντού, και τα σύνολα βγαίνουν με ένα
 * ερώτημα αντί για άθροισμα 20 καταστημάτων.
 *
 * Νέα εμφάνιση (14/10/2026): «Πίνακας» (shared/listPage) — λίστα με τα σύνολα όλης της αλυσίδας
 * ανά προσφορά, και συρτάρι δεξιά με τη φόρμα (ή τα στοιχεία, για όποιον δεν διαχειρίζεται).
 * Ίδια endpoints, ίδιο σώμα αποθήκευσης, ίδιοι έλεγχοι με πριν· η διαγραφή ρωτά με το ConfirmModal
 * αντί για το παράθυρο του browser.
 */

import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'lucide-react';
import { organizationAPI } from '../services/api';
import { formatMoney } from '../utils/formatMoney';
import ConfirmModal from './shared/ConfirmModal';
import { LoadFailed } from './settings/instantSettings';
import { SettingsSwitch } from './settings/storeSettingsForm';
import { SheetField, SheetRow } from './settings/sheet';
import {
  Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, KeyValue, KeyValueItem,
  ListPage, ListToolbar, Notice, PRIMARY_BTN, SELECT_CLS, StatusChip, matchesSearch,
} from './shared/listPage';
import { Banner, GroupTitle, chipClass } from './orgPages/ui';

interface Props {
  orgId: number;
  canManage: boolean;
}

type PromotionType = 'percentage' | 'fixed_amount' | 'buy_x_get_y' | 'bundle_price' | 'order_fixed_amount';

// Η σειρά των τύπων στη φόρμα (ίδια με πριν: το κουπόνι αξίας πρώτο, είναι η προεπιλογή).
const TYPES: PromotionType[] = ['order_fixed_amount', 'percentage', 'fixed_amount', 'buy_x_get_y', 'bundle_price'];

// Κυριακή πρώτη, όπως πάντα: οι τιμές είναι του PostgreSQL (EXTRACT DOW: 0 = Κυριακή).
const DAYS = [0, 1, 2, 3, 4, 5, 6];

interface Promotion {
  id: number;
  name: string;
  description?: string | null;
  promotion_type: PromotionType;
  discount_value?: number | null;
  bundle_price?: number | null;
  buy_quantity?: number | null;
  get_quantity?: number | null;
  min_order_amount: number;
  starts_at?: string | null;
  ends_at?: string | null;
  days_of_week?: number[] | null;
  time_from?: string | null;
  time_to?: string | null;
  is_active: boolean;
  org_product_ids: number[];
}

const EMPTY = {
  name: '', description: '', promotion_type: 'order_fixed_amount' as PromotionType,
  discount_value: 3 as number | null, bundle_price: null as number | null,
  buy_quantity: null as number | null, get_quantity: null as number | null,
  min_order_amount: 0, starts_at: '', ends_at: '',
  days_of_week: [] as number[], time_from: '', time_to: '',
  is_active: true, org_product_ids: [] as number[],
};

type LoadState = 'loading' | 'ready' | 'error' | 'forbidden';
/** Τι δείχνει το συρτάρι: νέα προσφορά, μία υπάρχουσα, ή τίποτα. */
type Drawer = { mode: 'new' } | { mode: 'edit'; promotion: Promotion } | null;

const ymd = (v: string | null | undefined): string => (v ? String(v).slice(0, 10) : '');
const dmy = (v: string): string => {
  const [y, m, d] = v.split('-');
  return y && m && d ? `${d}/${m}/${y}` : v;
};

const OrgPromotionsManager: React.FC<Props> = ({ orgId, canManage }) => {
  const { t } = useTranslation(['orgPages', 'common']);
  const uid = useId();
  const formId = `${uid}-form`;
  const [promos, setPromos] = useState<Promotion[]>([]);
  const [products, setProducts] = useState<Array<{ id: number; name: string }>>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [load, setLoad] = useState<LoadState>('loading');
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [productQuery, setProductQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Promotion | null>(null);

  const load_ = useCallback(async () => {
    setLoad('loading');
    try {
      const [p, a] = await Promise.all([
        organizationAPI.getPromotions(orgId),
        organizationAPI.getPromotionAnalytics(orgId).catch(() => null),
      ]);
      setPromos(p.promotions || []);
      setAnalytics(a);
      setLoad('ready');
    } catch (e: any) {
      setLoad(e?.response?.status === 403 ? 'forbidden' : 'error');
    }
  }, [orgId]);

  useEffect(() => { load_(); }, [load_]);

  useEffect(() => {
    organizationAPI.getCatalogProducts(orgId)
      .then((r: any) => setProducts((r.products || r || []).map((x: any) => ({ id: x.id, name: x.name }))))
      .catch(() => setProducts([]));
  }, [orgId]);

  const typeLabel = (type: PromotionType) => t(`orgPages:promotions.types.${type}`);

  const openNew = () => {
    setForm({ ...EMPTY });
    setError(null);
    setProductQuery('');
    setDrawer({ mode: 'new' });
  };
  const openEdit = (p: Promotion) => {
    setForm({
      name: p.name, description: p.description || '', promotion_type: p.promotion_type,
      discount_value: p.discount_value ?? null, bundle_price: p.bundle_price ?? null,
      buy_quantity: p.buy_quantity ?? null, get_quantity: p.get_quantity ?? null,
      min_order_amount: p.min_order_amount || 0,
      starts_at: ymd(p.starts_at),
      ends_at: ymd(p.ends_at),
      days_of_week: p.days_of_week || [], time_from: p.time_from || '', time_to: p.time_to || '',
      is_active: p.is_active, org_product_ids: p.org_product_ids || [],
    });
    setError(null);
    setProductQuery('');
    setDrawer({ mode: 'edit', promotion: p });
  };
  const closeDrawer = () => {
    // Το Esc μέσα από το παράθυρο επιβεβαίωσης κλείνει ΠΡΩΤΑ εκείνο (όχι και το συρτάρι από κάτω).
    if (confirmDelete) { setConfirmDelete(null); return; }
    setDrawer(null);
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return t('orgPages:promotions.errors.name');
    const v = Number(form.discount_value);
    if (form.promotion_type === 'percentage' && !(v > 0 && v <= 100)) return t('orgPages:promotions.errors.percent');
    if (form.promotion_type === 'fixed_amount' && !(v > 0)) return t('orgPages:promotions.errors.fixed');
    if (form.promotion_type === 'order_fixed_amount' && !(v > 0)) return t('orgPages:promotions.errors.coupon');
    if (form.promotion_type === 'buy_x_get_y' && (!form.buy_quantity || !form.get_quantity)) return t('orgPages:promotions.errors.buyGet');
    if (form.promotion_type === 'bundle_price' && !(Number(form.bundle_price) >= 0)) return t('orgPages:promotions.errors.bundle');
    // Μόνο το κουπόνι αξίας μπορεί να ισχύει σε ολόκληρη την παραγγελία.
    if (form.promotion_type !== 'order_fixed_amount' && form.org_product_ids.length === 0) return t('orgPages:promotions.errors.products');
    return null;
  };

  const save = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        discount_value: ['percentage', 'fixed_amount', 'order_fixed_amount'].includes(form.promotion_type)
          ? Number(form.discount_value) : null,
        bundle_price: form.promotion_type === 'bundle_price' ? Number(form.bundle_price) : null,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        time_from: form.time_from || null,
        time_to: form.time_to || null,
      };
      if (drawer?.mode === 'edit') await organizationAPI.updatePromotion(orgId, drawer.promotion.id, payload);
      else await organizationAPI.createPromotion(orgId, payload);
      setDrawer(null);
      await load_();
    } catch (e: any) {
      setError(e?.response?.data?.error || t('orgPages:promotions.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const p = confirmDelete;
    setConfirmDelete(null);
    if (!p) return;
    try {
      await organizationAPI.deletePromotion(orgId, p.id);
      setDrawer(null);
      await load_();
    } catch {
      setError(t('orgPages:promotions.errors.delete'));
    }
  };

  const describe = useCallback((p: Promotion): string => {
    switch (p.promotion_type) {
      case 'order_fixed_amount': return t('orgPages:promotions.describe.coupon', { amount: formatMoney(Number(p.discount_value)) });
      case 'percentage': return t('orgPages:promotions.describe.percent', { value: p.discount_value });
      case 'fixed_amount': return t('orgPages:promotions.describe.perItem', { amount: formatMoney(Number(p.discount_value)) });
      case 'buy_x_get_y': return t('orgPages:promotions.describe.buyGet', { buy: p.buy_quantity, get: p.get_quantity });
      case 'bundle_price': return t('orgPages:promotions.describe.bundle', { amount: formatMoney(Number(p.bundle_price)) });
      default: return '';
    }
  }, [t]);

  /** «σε όλη την παραγγελία · ελάχιστο 10,00 €» — η δεύτερη γραμμή της λίστας. */
  const scope = useCallback((p: Promotion): string => [
    typeLabel(p.promotion_type),
    p.org_product_ids.length > 0
      ? t('orgPages:promotions.meta.products', { count: p.org_product_ids.length })
      : p.promotion_type === 'order_fixed_amount' ? t('orgPages:promotions.meta.wholeOrder') : '',
    Number(p.min_order_amount) > 0 ? t('orgPages:promotions.meta.minimum', { amount: formatMoney(Number(p.min_order_amount)) }) : '',
  ].filter(Boolean).join(' · '), [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const statFor = useCallback((id: number) => (analytics?.promotions || []).find((x: any) => x.id === id), [analytics]);
  const storesFor = useCallback((id: number) => (analytics?.by_store || []).filter((x: any) => x.promotion_id === id), [analytics]);

  const columns = useMemo<Column<Promotion>[]>(() => [
    {
      key: 'name',
      header: t('orgPages:promotions.columns.promotion'),
      width: 'minmax(220px, 2.4fr)',
      render: p => (
        <>
          <span className="block font-medium text-t1">{p.name}</span>
          <span className="block text-xs text-t3">{scope(p)}</span>
        </>
      ),
      stackRender: p => p.name,
    },
    {
      key: 'value',
      header: t('orgPages:promotions.columns.value'),
      width: 'minmax(130px, 1fr)',
      render: p => <span className="font-medium tabular-nums text-t1">{describe(p)}</span>,
      stackRender: p => describe(p),
    },
    {
      key: 'status',
      header: t('orgPages:promotions.columns.status'),
      width: '120px',
      render: p => (p.is_active
        ? <StatusChip tone="ok">{t('orgPages:promotions.active')}</StatusChip>
        : <StatusChip tone="muted">{t('orgPages:promotions.inactive')}</StatusChip>),
    },
    {
      key: 'uses',
      header: t('orgPages:promotions.columns.uses'),
      width: '96px',
      align: 'right',
      hideBelow: 'medium',
      render: p => <span className="text-t2">{Number(statFor(p.id)?.uses) || 0}</span>,
    },
    {
      key: 'discount',
      header: t('orgPages:promotions.columns.discount'),
      width: '140px',
      align: 'right',
      stack: 'end',
      render: p => <span className="font-medium text-t1">{formatMoney(Number(statFor(p.id)?.total_discount) || 0)}</span>,
    },
    {
      key: 'stores',
      header: t('orgPages:promotions.columns.stores'),
      width: '110px',
      align: 'right',
      hideBelow: 'medium',
      render: p => {
        const n = storesFor(p.id).length;
        return n > 0 ? <span className="text-t2">{n}</span> : <span className="text-t4">—</span>;
      },
    },
  ], [t, scope, describe, statFor, storesFor]);

  const productName = (id: number) => products.find(p => p.id === id)?.name || `#${id}`;
  const shownProducts = products.filter(p => matchesSearch(productQuery, p.name));

  const daysText = (days: number[] | null | undefined): string => (days && days.length > 0
    ? DAYS.filter(d => days.includes(d)).map(d => t(`orgPages:days.${d}`)).join(', ')
    : t('orgPages:promotions.detail.everyDay'));

  const periodText = (p: Promotion): string => {
    const from = ymd(p.starts_at);
    const to = ymd(p.ends_at);
    if (from && to) return `${dmy(from)} – ${dmy(to)}`;
    if (from) return t('orgPages:promotions.detail.from', { date: dmy(from) });
    if (to) return t('orgPages:promotions.detail.until', { date: dmy(to) });
    return t('orgPages:promotions.detail.always');
  };

  /** Τα σύνολα της αλυσίδας για μία προσφορά (το κέρδος της μίας εγγραφής): χρήσεις, έκπτωση, ανά κατάστημα. */
  const statsBlock = (p: Promotion) => {
    const st = statFor(p.id);
    const byStore = storesFor(p.id);
    return (
      <section className="space-y-3" data-testid="promotion-stats">
        <GroupTitle>{t('orgPages:promotions.stats.title')}</GroupTitle>
        <KeyValue items={[
          { id: 'uses', label: t('orgPages:promotions.stats.usesLabel'), value: t('orgPages:promotions.stats.uses', { count: Number(st?.uses) || 0 }) },
          { id: 'total', label: t('orgPages:promotions.stats.total'), value: formatMoney(Number(st?.total_discount) || 0) },
          ...(byStore.length > 0 ? [{
            id: 'stores',
            label: t('orgPages:promotions.stats.byStore'),
            value: (
              <ul className="space-y-1">
                {byStore.map((s: any) => (
                  <li key={s.store_id} className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate font-normal text-t2">{s.store}</span>
                    <span className="shrink-0 tabular-nums">{formatMoney(Number(s.total_discount) || 0)} <span className="text-t3 font-normal">({s.uses})</span></span>
                  </li>
                ))}
              </ul>
            ),
          }] : []),
        ]} />
      </section>
    );
  };

  /** Για όποιον δεν διαχειρίζεται: τα στοιχεία της προσφοράς, χωρίς φόρμα. */
  const detailItems = (p: Promotion): KeyValueItem[] => [
    { id: 'status', label: t('orgPages:promotions.detail.status'), value: p.is_active ? t('orgPages:promotions.active') : t('orgPages:promotions.inactive') },
    { id: 'type', label: t('orgPages:promotions.detail.type'), value: typeLabel(p.promotion_type) },
    { id: 'value', label: t('orgPages:promotions.detail.value'), value: describe(p) },
    {
      id: 'products',
      label: t('orgPages:promotions.detail.products'),
      value: p.org_product_ids.length > 0
        ? p.org_product_ids.map(productName).join(', ')
        : (p.promotion_type === 'order_fixed_amount' ? t('orgPages:promotions.meta.wholeOrder') : null),
    },
    { id: 'min', label: t('orgPages:promotions.detail.minimum'), value: Number(p.min_order_amount) > 0 ? formatMoney(Number(p.min_order_amount)) : null },
    { id: 'period', label: t('orgPages:promotions.detail.period'), value: periodText(p) },
    { id: 'days', label: t('orgPages:promotions.detail.days'), value: daysText(p.days_of_week) },
    ...(p.time_from && p.time_to ? [{ id: 'hours', label: t('orgPages:promotions.detail.hours'), value: `${p.time_from}–${p.time_to}` }] : []),
  ];

  const numberInput = (
    id: string, value: number | null, onChange: (v: number | null) => void,
    opts: { step?: number; min?: number; max?: number; integer?: boolean; placeholder?: string } = {},
  ) => (
    <input
      id={id}
      type="number"
      min={opts.min ?? 0}
      max={opts.max}
      step={opts.step ?? 0.01}
      value={value ?? ''}
      placeholder={opts.placeholder}
      onChange={e => onChange(e.target.value === '' ? null : (opts.integer ? parseInt(e.target.value, 10) : parseFloat(e.target.value)))}
      className={`${INPUT_CLS} max-w-[12rem] tabular-nums`}
    />
  );

  const editing = drawer?.mode === 'edit' ? drawer.promotion : null;
  const readOnlyDrawer = !!editing && !canManage;

  const formBody = (
    <form id={formId} onSubmit={save} className="space-y-5" noValidate>
      {error && <Notice tone="danger" testId="promotion-form-error">{error}</Notice>}

      <SheetField label={t('orgPages:promotions.form.name')} htmlFor={`${uid}-name`}>
        <input id={`${uid}-name`} type="text" value={form.name} autoFocus={drawer?.mode === 'new'}
          onChange={e => setForm({ ...form, name: e.target.value })}
          placeholder={t('orgPages:promotions.form.namePlaceholder')} className={INPUT_CLS} />
      </SheetField>

      <SheetField label={t('orgPages:promotions.form.type')} htmlFor={`${uid}-type`}>
        <select id={`${uid}-type`} value={form.promotion_type}
          onChange={e => setForm({ ...form, promotion_type: e.target.value as PromotionType })}
          className={SELECT_CLS}>
          {TYPES.map(type => <option key={type} value={type}>{typeLabel(type)}</option>)}
        </select>
      </SheetField>

      {form.promotion_type === 'order_fixed_amount' && (
        <SheetField label={t('orgPages:promotions.form.couponValue')} htmlFor={`${uid}-value`} hint={t('orgPages:promotions.form.couponHint')}>
          {numberInput(`${uid}-value`, form.discount_value, v => setForm({ ...form, discount_value: v }))}
        </SheetField>
      )}
      {form.promotion_type === 'percentage' && (
        <SheetField label={t('orgPages:promotions.form.percent')} htmlFor={`${uid}-value`}>
          {numberInput(`${uid}-value`, form.discount_value, v => setForm({ ...form, discount_value: v }), { step: 1, max: 100 })}
        </SheetField>
      )}
      {form.promotion_type === 'fixed_amount' && (
        <SheetField label={t('orgPages:promotions.form.perItem')} htmlFor={`${uid}-value`}>
          {numberInput(`${uid}-value`, form.discount_value, v => setForm({ ...form, discount_value: v }))}
        </SheetField>
      )}
      {form.promotion_type === 'buy_x_get_y' && (
        <div className="xs-grid2">
          <SheetField label={t('orgPages:promotions.form.buy')} htmlFor={`${uid}-buy`}>
            {numberInput(`${uid}-buy`, form.buy_quantity, v => setForm({ ...form, buy_quantity: v }), { step: 1, min: 1, integer: true })}
          </SheetField>
          <SheetField label={t('orgPages:promotions.form.get')} htmlFor={`${uid}-get`}>
            {numberInput(`${uid}-get`, form.get_quantity, v => setForm({ ...form, get_quantity: v }), { step: 1, min: 1, integer: true })}
          </SheetField>
        </div>
      )}
      {form.promotion_type === 'bundle_price' && (
        <SheetField label={t('orgPages:promotions.form.bundlePrice')} htmlFor={`${uid}-bundle`}>
          {numberInput(`${uid}-bundle`, form.bundle_price, v => setForm({ ...form, bundle_price: v }))}
        </SheetField>
      )}

      <SheetField label={t('orgPages:promotions.form.minOrder')} htmlFor={`${uid}-min`}>
        <input id={`${uid}-min`} type="number" min={0} step={0.01} value={form.min_order_amount || ''}
          onChange={e => setForm({ ...form, min_order_amount: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
          placeholder={t('orgPages:promotions.form.minOrderPlaceholder')} className={`${INPUT_CLS} max-w-[12rem] tabular-nums`} />
      </SheetField>

      <div className="xs-grid2">
        <SheetField label={t('orgPages:promotions.form.from')} htmlFor={`${uid}-from`}>
          <input id={`${uid}-from`} type="date" value={form.starts_at} onChange={e => setForm({ ...form, starts_at: e.target.value })} className={INPUT_CLS} />
        </SheetField>
        <SheetField label={t('orgPages:promotions.form.to')} htmlFor={`${uid}-to`}>
          <input id={`${uid}-to`} type="date" value={form.ends_at} onChange={e => setForm({ ...form, ends_at: e.target.value })} className={INPUT_CLS} />
        </SheetField>
      </div>

      <SheetField label={t('orgPages:promotions.form.days')}>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('orgPages:promotions.form.days')}>
          {DAYS.map(d => {
            const on = form.days_of_week.includes(d);
            return (
              <button key={d} type="button" aria-pressed={on} className={chipClass(on)}
                onClick={() => setForm({ ...form, days_of_week: on ? form.days_of_week.filter(x => x !== d) : [...form.days_of_week, d] })}>
                {t(`orgPages:days.${d}`)}
              </button>
            );
          })}
        </div>
      </SheetField>

      {/* Προϊόντα — ΚΕΝΤΡΙΚΑ, μεταφράζονται ανά κατάστημα */}
      <SheetField
        label={form.promotion_type === 'order_fixed_amount' ? t('orgPages:promotions.form.productsOptional') : t('orgPages:promotions.form.products')}
        hint={form.promotion_type === 'order_fixed_amount' ? t('orgPages:promotions.form.productsHintCoupon') : t('orgPages:promotions.form.productsHint')}
      >
        <div className="rounded-xl border border-line">
          <div className="flex items-center gap-3 border-b border-line p-2">
            <input type="search" value={productQuery} onChange={e => setProductQuery(e.target.value)}
              aria-label={t('orgPages:promotions.form.productsSearch')} placeholder={t('orgPages:promotions.form.productsSearch')}
              className={`${INPUT_CLS} min-h-[40px] py-2`} />
            <span className="shrink-0 pr-1 text-xs tabular-nums text-t3">
              {t('orgPages:promotions.form.selected', { count: form.org_product_ids.length })}
            </span>
          </div>
          <div className="max-h-56 overflow-y-auto p-1.5">
            {shownProducts.length === 0 && <p className="p-2 text-sm text-t4">{t('orgPages:promotions.form.noProducts')}</p>}
            {shownProducts.map(pr => {
              const on = form.org_product_ids.includes(pr.id);
              return (
                <label key={pr.id} className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm ${on ? 'bg-brand-soft text-t1' : 'text-t2 hover:bg-surface-2'}`}>
                  <input type="checkbox" checked={on} className="h-4 w-4 shrink-0 accent-[hsl(var(--brand))]"
                    onChange={() => setForm({
                      ...form,
                      org_product_ids: on ? form.org_product_ids.filter(x => x !== pr.id) : [...form.org_product_ids, pr.id],
                    })} />
                  <span className="truncate">{pr.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      </SheetField>

      <SheetRow
        title={t('orgPages:promotions.form.active')}
        subtitle={t('orgPages:promotions.form.activeHint')}
        control={({ labelId, descriptionId }) => (
          <SettingsSwitch checked={form.is_active} onChange={next => setForm({ ...form, is_active: next })}
            labelId={labelId} describedById={descriptionId} />
        )}
      />

      {editing && statsBlock(editing)}
    </form>
  );

  const firstLoad = load === 'loading' && promos.length === 0;

  return (
    <ListPage
      eyebrow={t('orgDashboard:nav.groups.catalog')}
      title={t('orgPages:promotions.title')}
      subtitle={t('orgPages:promotions.subtitle')}
      testId="org-promotions"
    >
      <ListToolbar
        actions={canManage ? (
          <button type="button" className={PRIMARY_BTN} onClick={openNew}>{t('orgPages:promotions.new')}</button>
        ) : null}
        summary={load === 'ready' && !firstLoad ? t('orgPages:promotions.count', { count: promos.length }) : null}
      />

      {error && !drawer && (
        <Banner tone="danger" role="alert" className="mb-3" onDismiss={() => setError(null)} dismissLabel={t('orgPages:common.dismiss')}>
          {error}
        </Banner>
      )}

      {load === 'error' || load === 'forbidden' ? (
        <LoadFailed
          message={load === 'forbidden' ? t('orgPages:promotions.forbidden') : t('orgPages:promotions.loadFailed')}
          onRetry={load_}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={promos}
          rowKey={p => p.id}
          onRowClick={openEdit}
          selectedKey={editing?.id ?? null}
          loading={load === 'loading'}
          rowLabel={p => t('orgPages:promotions.rowLabel', { name: p.name })}
          rowClassName={p => (p.is_active ? undefined : 'opacity-70')}
          empty={(
            <EmptyState
              icon={Tag}
              title={t('orgPages:promotions.empty')}
              hint={canManage ? t('orgPages:promotions.emptyHint') : undefined}
              action={canManage ? <button type="button" className={GHOST_BTN} onClick={openNew}>{t('orgPages:promotions.new')}</button> : undefined}
            />
          )}
          testId="promotions-table"
        />
      )}

      <DetailPanel
        open={drawer !== null}
        onClose={closeDrawer}
        width="lg"
        title={drawer?.mode === 'new'
          ? t('orgPages:promotions.form.newTitle')
          : (readOnlyDrawer ? editing?.name : t('orgPages:promotions.form.editTitle'))}
        subtitle={editing ? (readOnlyDrawer ? describe(editing) : editing.name) : undefined}
        footer={readOnlyDrawer ? (
          <button type="button" className={GHOST_BTN} onClick={() => setDrawer(null)}>{t('orgPages:common.close')}</button>
        ) : (
          <>
            <button type="button" className={GHOST_BTN} onClick={() => setDrawer(null)}>{t('orgPages:common.cancel')}</button>
            <button type="submit" form={formId} className={PRIMARY_BTN} disabled={saving}>
              {editing ? t('orgPages:common.save') : t('orgPages:common.create')}
            </button>
          </>
        )}
        footerStart={editing && canManage ? (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => setConfirmDelete(editing)}>{t('orgPages:common.delete')}</button>
        ) : undefined}
        testId="promotion-panel"
      >
        {readOnlyDrawer && editing ? (
          <div className="space-y-6">
            <KeyValue items={detailItems(editing)} testId="promotion-details" />
            {statsBlock(editing)}
          </div>
        ) : drawer ? formBody : null}
      </DetailPanel>

      <ConfirmModal
        isOpen={!!confirmDelete}
        variant="danger"
        title={t('orgPages:promotions.delete.title', { name: confirmDelete?.name || '' })}
        message={t('orgPages:promotions.delete.message')}
        confirmLabel={t('orgPages:common.delete')}
        cancelLabel={t('orgPages:common.cancel')}
        onConfirm={remove}
        onCancel={() => setConfirmDelete(null)}
      />
    </ListPage>
  );
};

export default OrgPromotionsManager;
